import { useEffect } from 'react';

/**
 * Library-free per-route SEO. Updates <title>, meta description, Open Graph /
 * Twitter tags, canonical, an optional per-page social image, and an optional
 * BreadcrumbList JSON-LD. The base LocalBusiness/Organization/WebSite schema
 * lives in public/index.html.
 */
const SITE = 'https://millpondgatherings.com';
const DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1762015101892-46496da449b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, key, val] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
    if (key && val) el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const Seo = ({ title, description, path = '', image, breadcrumbs, keywords }) => {
  useEffect(() => {
    const url = `${SITE}${path}`;
    const img = image || DEFAULT_IMAGE;

    if (title) {
      document.title = title;
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (keywords) {
      setMeta('meta[name="keywords"]', 'content', keywords);
    }
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', img);
    setMeta('meta[name="twitter:image"]', 'content', img);
    setLink('canonical', url);

    setJsonLd(
      'ld-breadcrumb',
      breadcrumbs && breadcrumbs.length
        ? {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((b, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: b.name,
              item: `${SITE}${b.path}`,
            })),
          }
        : null,
    );
  }, [title, description, path, image, breadcrumbs, keywords]);

  return null;
};

export default Seo;
